import { API } from "aws-amplify";
import { getProduct, getProductBundle } from "@/graphql/queries";
import { onCreateJustBoughtProduct } from "@/graphql/subscriptions";
import { mapState, mapGetters } from "vuex";

export default {
  data: () => ({
    allowedJustBoughtCategories: [],
    justBoughtProductCreateSub: null,
    lastJustBoughtShownTimestamp: null,
  }),

  computed: {
    ...mapState({
      syncedTeacherProfileModel: (state) => state.syncedTeacherProfileModel,
      syncedBoughtProducts: (state) => state.syncedBoughtProducts,
      syncedTeacherProductsOrderModels: (state) =>
        state.syncedTeacherProductsOrderModels,
      userSignedIn: (state) => state.userSignedIn,
    }),

    ...mapGetters(["pendingProducts"]),
  },

  methods: {
    isProductInOrdersOrPending(id) {
      return this.syncedTeacherProductsOrderModels
        ?.filter((order) => order.orderStatus !== "CANCELLED")
        .flatMap(
          (order) => order.orderProducts?.map((product) => product.id) || [],
        )
        .some((productId) => productId === id) ||
        this.syncedTeacherProfileModel?.productSubscriptions?.some(
          (subscription) => subscription.productId === id,
        ) ||
        this.pendingProducts.some((pendingId) => pendingId === id)
        ? true
        : false;
    },

    async syncJustBoughtProducts(categories = []) {
      this.allowedJustBoughtCategories = categories;

      // Setup subscription only once. If auth state changed since the
      // subscription was first created (e.g. guest → signed-in on same page
      // load) tear it down so it can be rebuilt with the correct authMode.
      if (this.justBoughtProductCreateSub) {
        // Already subscribed — just update the allowed categories so the
        // existing next() handler picks them up reactively. No need to
        // re-subscribe unless the authMode needs to change.
        // console.log(
        //   "syncJustBoughtProducts - updated allowedJustBoughtCategories:",
        //   categories,
        // );
        return;
      }

      this.setupJustBoughtSubscription();
    },

    setupJustBoughtSubscription() {
      // Resolve the correct auth mode at the moment the subscription is
      // created. Logged-out (guest) users must use AWS_IAM; logged-in users
      // use Cognito User Pools.
      const authMode = this.userSignedIn
        ? "AMAZON_COGNITO_USER_POOLS"
        : "AWS_IAM";

      // console.log(
      //   `setupJustBoughtSubscription - authMode: ${authMode}, categories:`,
      //   this.allowedJustBoughtCategories,
      // );

      this.justBoughtProductCreateSub = API.graphql({
        query: onCreateJustBoughtProduct,
        authMode,
      }).subscribe({
        next: async ({ value }) => {
          const newItem = this.parseGraphqlResult(
            value.data.onCreateJustBoughtProduct,
          );

          // console.log(
          //   "Received onCreateJustBoughtProduct subscription event:",
          //   newItem,
          // );

          // ── 1. Cooldown check ──────────────────────────────────────────
          const now = Date.now();
          const ONE_MINUTE = 60 * 1000;

          if (
            this.lastJustBoughtShownTimestamp &&
            now - this.lastJustBoughtShownTimestamp < ONE_MINUTE
          ) {
            // console.log(
            //   "JustBoughtProduct snackbar skipped – still in 1-minute cooldown",
            // );
            return;
          }

          // ── 2. Guard: invalid item ─────────────────────────────────────
          if (!newItem) return;

          // ── 3. Category filter (logged-in users only) ──────────────────
          // Logged-out guests see all justBoughtProducts regardless of category.
          // Logged-in users only see products whose categoryId is included in
          // their teacherProfile's smartProductSort.categories.
          if (
            this.userSignedIn &&
            !this.allowedJustBoughtCategories.includes(newItem.categoryId)
          ) {
            // console.log(
            //   "JustBoughtProduct skipped – categoryId not in allowed categories:",
            //   newItem.categoryId,
            // );
            return;
          }

          // ── 4. Skip if the user already owns / has pending this product ─
          if (
            this.isProductInOrdersOrPending(
              newItem.productID || newItem.productBundleID,
            )
          ) {
            return;
          }

          // ── 5. Fetch the full Product or Bundle record ─────────────────
          const fetchAuthMode = this.userSignedIn
            ? "AMAZON_COGNITO_USER_POOLS"
            : "AWS_IAM";

          try {
            if (newItem.productID) {
              const result = await API.graphql({
                query: getProduct,
                variables: { id: newItem.productID },
                authMode: fetchAuthMode,
              });
              newItem.Product = result?.data?.getProduct || null;
            } else if (newItem.productBundleID) {
              const result = await API.graphql({
                query: getProductBundle,
                variables: { id: newItem.productBundleID },
                authMode: fetchAuthMode,
              });
              newItem.ProductBundle = result?.data?.getProductBundle || null;
            }
          } catch (e) {
            console.error(
              "Failed to fetch Product/Bundle for JustBoughtProduct:",
              e,
            );
          }

          // ── 6. Show snackbar and start cooldown ────────────────────────
          this.showJustBoughtSnackbar(newItem);
          this.lastJustBoughtShownTimestamp = now;
        },
        error: (error) => {
          console.error("onCreateJustBoughtProduct error:", error);
        },
      });
    },
  },

  beforeDestroy() {
    if (this.justBoughtProductCreateSub) {
      this.justBoughtProductCreateSub.unsubscribe();
    }
  },
};
